import type { CopyKey } from './i18n';
/* `.js`, like `chat/suggest.ts` imports `./kinds.js`: this module is compiled for plain Node
   as well as for the bundler, and Node's ESM resolver does not guess extensions. */
import { ROUTES } from './routes.js';

/**
 * Where the app can be navigated to, and what each of those places is called in CSS.
 *
 * **Every path here comes from `ROUTES`, and the type is what enforces it.** `Path` is the
 * union of the literal strings in that table, so a section spelling its own address — even a
 * correct one — is a compile error rather than a menu entry that leads nowhere. That is not a
 * hypothetical: this file was written with `/investigacion` and `/ajustes` typed into it, F-142
 * renamed both a branch away, and the two would have met in `main` with the menu still working
 * (React Router would have matched nothing and rendered the 404) and the desktop layout for
 * those screens silently gone.
 *
 * The second column is the reason this file exists. F-136 gives the stage a `data-screen`
 * attribute so the stylesheet can say something per screen — how wide the conversation may
 * run, which screens may put their panels two-up — and the obvious way to write that is a
 * second map from path to name. Two lists of the same routes drift the first time a route is
 * renamed, and the drift is silent in the worst way this project has a name for: the attribute
 * falls back to `page`, every rule keyed on that screen stops matching, and nothing anywhere
 * reports an error. The layout simply goes back to being a phone on a desk.
 *
 * So the screen name lives on the section, `screenOf` is the only way to ask, and
 * `nav.test.ts` asserts both halves: that every destination has a name, and that every
 * destination is an address `routes.ts` actually publishes.
 *
 * React-free on purpose: `tsconfig.test.json` compiles this file for plain Node, which is what
 * lets all of that be asserted without a DOM.
 */
export type Path = (typeof ROUTES)[keyof typeof ROUTES];

export type Section = {
  readonly to: Path;
  readonly label: CopyKey;
  /**
   * What `styles.css` calls this screen. English, and deliberately not the path: a stylesheet
   * cannot ask the router, and a selector spelling `/settings` would be a third place the
   * address is written down — which is what F-142 has just finished removing.
   */
  readonly screen: string;
};

/** The conversation. Not a section — it is above them in the menu and in the bar. */
export const CHAT: Section = { to: ROUTES.chat, label: 'nav.chat', screen: 'chat' };

/** The five sections of the app, in menu order. */
export const SECTIONS: readonly Section[] = [
  { to: ROUTES.progress, label: 'nav.progress', screen: 'progress' },
  { to: ROUTES.plan, label: 'nav.plan', screen: 'plan' },
  { to: ROUTES.research, label: 'nav.data', screen: 'research' },
  { to: ROUTES.settings, label: 'nav.config', screen: 'config' },
  { to: ROUTES.profile, label: 'nav.profile', screen: 'profile' },
];

/**
 * What to put on `main.stage`'s `data-screen` for a path.
 *
 * `page` for everything else, and that covers more than a 404: the front page, the sign-in
 * screen, the unsubscribe landing and the console all land there. None of them wants a rule of
 * its own — they are a single panel or a piece of marketing, and the shell's defaults are
 * already right for both. **The console is the one that has to be checked when a default
 * changes**, because it is a ten-column table wearing a screen name that means "nothing
 * special"; `console.css` widens the page for it through `--page-w` and nothing keyed on
 * `data-screen` may narrow it again.
 */
export function screenOf(pathname: string): string {
  if (pathname === CHAT.to) return CHAT.screen;
  return SECTIONS.find((section) => section.to === pathname)?.screen ?? 'page';
}
